import { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface CommentInputProps {
  onSubmit: (content: string) => void;
  isSubmitting?: boolean;
  className?: string;
}

const MAX_CHARACTERS = 300;

export function CommentInput({ onSubmit, isSubmitting = false, className }: CommentInputProps) {
  const [content, setContent] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const characterCount = content.length;
  const isOverLimit = characterCount > MAX_CHARACTERS;
  const canSubmit = content.trim().length > 0 && !isOverLimit && !isSubmitting;

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(content.trim());
      setContent('');
      setIsExpanded(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    if (!content.trim()) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isExpanded]);

  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-end w-full gap-1 relative">
        <div className="relative flex-1">
          <div className="relative w-full bg-[#362B2B] rounded-2xl overflow-hidden">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Write a comment..."
          className={cn(
                "min-h-[40px] max-h-[160px] resize-none",
                "bg-transparent text-white !placeholder-white",
                "border-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                "px-5 py-2 box-border comment-selection",
                isExpanded ? "min-h-[60px]" : "min-h-[40px]"
          )}
          maxLength={MAX_CHARACTERS}
              style={{ minHeight: '40px', maxHeight: '60px' }}
            />
            <div className="w-full px-4 py-1 flex justify-end">
              <span className="text-xs text-white/60 pointer-events-none">
              {characterCount}/{MAX_CHARACTERS}
            </span>
            </div>
          </div>
        </div>
          <Button
          type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
          className="shrink-0 rounded-full h-10 px-4 ml-1 bg-sossiety-red-600 hover:bg-sossiety-red-700 text-white text-xs font-bold shadow-none flex items-center justify-center"
        >
          {isSubmitting ? '...' : 'Post'}
          </Button>
      </div>
    </div>
  );
} 